// PRD 11 §2.2 #19 / §3.6 — 의료진 웹 미리보기. Browser-window card showing a
// live HTML render of the authored content. Text-block HTML is sanitized before
// `dangerouslySetInnerHTML`. Matches the prototype `updatePreview` rendering.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { sanitizeHtml } from '@/shared/utils/sanitize-html';
import { VideoBlockPreview } from '@/components/data/VideoBlockPreview';

import type { ContentBlock } from '../types/counseling-manual.types';

// Scroll bound for the preview card. PRD 11 §2.2 #19 specifies the browser-window
// card but no dimension, so this is an implementation choice — it mirrors the
// scrollable block-editor region on the left (ContentBlockList `maxHeight`) so the
// two columns stay visually balanced.
const PREVIEW_MAX_HEIGHT = 560;

interface WebPreviewProps {
  title: string;
  cid: string; // 'C-###' in edit mode, or a localized "auto-assigned" label in create mode.
  categoryPath: string; // 'area / subArea / item' (filtered of blanks).
  blocks: ContentBlock[];
  // SHCT-456 — "최종 수정" date. In edit mode this is the manual's DB updated_at
  // (passed by the panel); null in create mode (or until the BE populates it),
  // where the preview falls back to today.
  updatedAt?: string | null;
}

function todayKst(): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return fmt.format(new Date()).replace(/-/g, '.');
}

export function WebPreview({ title, cid, categoryPath, blocks, updatedAt }: WebPreviewProps) {
  const { t } = useTranslation('counseling-manual');
  const today = useMemo(todayKst, []);
  // Edit mode: the DB last-modified. Create mode (updatedAt null): today.
  const lastModified = updatedAt ?? today;

  return (
    // The preview must keep a stable footprint no matter how long the authored
    // content is (SHCT-448):
    //  • `minWidth: 0` — as a grid child it defaults to `min-width:auto`, so a
    //    long unbreakable token (e.g. a video URL) would expand the column and
    //    widen the whole panel. This lets it shrink; `overflowWrap` then breaks
    //    the token instead of pushing the box.
    //  • `maxHeight` + `overflowY:auto` — `overflowY` alone never scrolls
    //    without a height bound, so the card just grew taller and taller.
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--background)',
        minWidth: 0,
        maxHeight: PREVIEW_MAX_HEIGHT,
        overflowY: 'auto',
        overflowWrap: 'anywhere',
        padding: 24,
      }}
    >
      {/* Browser-like top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 12px',
          background: 'var(--muted)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', gap: 4 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }} />
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
        </div>
        <div
          style={{
            flex: 1,
            height: 22,
            background: 'var(--background)',
            borderRadius: 4,
            padding: '0 8px',
            fontSize: 11,
            color: 'var(--muted-foreground)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          smarthomecare.kr/manual/...
        </div>
      </div>

      <div
        style={{
          display: 'inline-block',
          padding: '3px 8px',
          background: '#eef6ff',
          color: '#1d4ed8',
          fontSize: 11,
          fontWeight: 600,
          borderRadius: 4,
          marginBottom: 12,
        }}
      >
        {categoryPath || t('preview.categoryPlaceholder')}
      </div>
      <h2
        style={{
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1.4,
          margin: '0 0 8px',
          color: 'var(--foreground)',
          overflowWrap: 'anywhere',
        }}
      >
        {title.trim() || t('preview.titlePlaceholder')}
      </h2>
      <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginBottom: 16 }}>
        CID: {cid} · {t('preview.lastModified')}: {lastModified}
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid var(--border)', marginBottom: 16 }} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, lineHeight: 1.7, color: 'var(--foreground)' }}>
        {blocks.length === 0 ? (
          <div style={{ color: 'var(--muted-foreground)' }}>{t('preview.empty')}</div>
        ) : (
          blocks.map((block) => <PreviewBlock key={block.id} block={block} />)
        )}
      </div>
    </div>
  );
}

function PreviewBlock({ block }: { block: ContentBlock }) {
  const { t } = useTranslation('counseling-manual');

  if (block.type === 'text') {
    const html = block.text.trim() ? sanitizeHtml(block.text) : '';
    return (
      <div>
        {block.title.trim() ? (
          <strong style={{ display: 'block', marginBottom: 4 }}>{block.title}</strong>
        ) : null}
        {html ? (
          <div className="mg-rte-render" dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <span style={{ color: 'var(--muted-foreground)' }}>{t('blocks.types.text')}</span>
        )}
      </div>
    );
  }

  if (block.type === 'image') {
    if (block.dataUrl) {
      return (
        <div>
          {block.title.trim() ? (
            <strong style={{ display: 'block', marginBottom: 4 }}>{block.title}</strong>
          ) : null}
          <img src={block.dataUrl} alt="" style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }} />
          {block.showText && block.text.trim() ? (
            <div style={{ marginTop: 4, fontSize: 13, color: 'var(--muted-foreground)', whiteSpace: 'pre-wrap' }}>
              {block.text}
            </div>
          ) : null}
        </div>
      );
    }
    return (
      <div
        style={{
          padding: 20,
          border: '1px dashed var(--border)',
          borderRadius: 8,
          background: '#f9fafb',
          textAlign: 'center',
          color: 'var(--muted-foreground)',
        }}
      >
        🖼 {block.title.trim() || t('blocks.types.image')}
      </div>
    );
  }

  // Video — URL only (ADM.018). Embed YouTube/Vimeo; SHCT-370: an unembeddable URL falls back
  // to the "영상 보기" card (PRD 11 v1.11 §3 [ADM.044]) instead of printing the address, which
  // the author could not open. A block with no URL keeps the placeholder.
  const url = block.url?.trim() ?? '';
  return (
    <div>
      {block.title.trim() ? (
        <strong style={{ display: 'block', marginBottom: 4 }}>{block.title}</strong>
      ) : null}
      {url ? (
        <VideoBlockPreview
          url={url}
          title={block.title.trim() || t('blocks.types.video')}
          watchLabel={t('preview.watchVideo')}
        />
      ) : (
        <div
          style={{
            padding: '8px 12px',
            background: '#eef2ff',
            borderRadius: 8,
            color: '#4338ca',
            fontSize: 13,
          }}
        >
          🎬 {t('preview.noVideoUrl')}
        </div>
      )}
      {block.showText && block.text.trim() ? (
        <div style={{ marginTop: 4, fontSize: 13, color: 'var(--muted-foreground)', whiteSpace: 'pre-wrap' }}>
          {block.text}
        </div>
      ) : null}
    </div>
  );
}
